import React, { useState } from "react";
import {
  Upload,
  Link as LinkIcon,
  AlertCircle,
  Sparkles,
  Image as ImageIcon,
  X,
} from "lucide-react";
import { api } from "./api_service/api";
import ImageResultModal from "./ImageResultModal";

type Tab = "upload" | "url";

const ImageDemo: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>("upload");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string>("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);

  const handleAnalyze = async () => {
    if (isAnalyzing) return;
    setError(null);
    setResult(null);
    setIsAnalyzing(true);

    try {
      await api.login();

      let data: any;
      if (activeTab === "url") {
        if (!imageUrl.trim()) throw new Error("Please enter an image URL");
        data = await api.analyzeImageUrl(imageUrl.trim());
      } else {
        if (!imageFile) throw new Error("Please select an image");
        data = await api.analyzeImageFile(imageFile);
      }

      console.log("Image analysis result →", data);
      setResult(data);
    } catch (err: any) {
      const msg = err instanceof Error ? err.message : "Analysis failed";
      console.error("Image analysis error:", err);
      setError(msg);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setImageFile(file);
      setError(null);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-pink-50 p-4 sm:p-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Tabs */}
            <div className="flex border-b border-indigo-200">
              <button
                onClick={() => setActiveTab("upload")}
                className={`flex-1 py-5 px-6 font-bold flex items-center justify-center gap-3 transition-all ${
                  activeTab === "upload"
                    ? "bg-indigo-600 text-white"
                    : "text-indigo-700 hover:bg-indigo-50"
                }`}
              >
                <Upload size={22} />
                Upload Image
              </button>
              <button
                onClick={() => setActiveTab("url")}
                className={`flex-1 py-5 px-6 font-bold flex items-center justify-center gap-3 transition-all ${
                  activeTab === "url"
                    ? "bg-indigo-600 text-white"
                    : "text-indigo-700 hover:bg-indigo-50"
                }`}
              >
                <LinkIcon size={22} />
                Paste URL
              </button>
            </div>

            <div className="p-8 min-h-96">
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-800">
                  <AlertCircle size={20} />
                  <span>{error}</span>
                </div>
              )}

              {!isAnalyzing && (
                <>
                  {/* Upload Tab */}
                  {activeTab === "upload" && (
                    <div className="space-y-8">
                      <div className="border-2 border-dashed border-indigo-300 rounded-2xl p-16 text-center hover:border-indigo-500 transition cursor-pointer group">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="hidden"
                          id="image-upload"
                        />
                        <label
                          htmlFor="image-upload"
                          className="cursor-pointer"
                        >
                          <ImageIcon
                            size={72}
                            className="mx-auto text-indigo-500 group-hover:text-indigo-600 mb-6"
                          />
                          <p className="text-xl font-semibold text-indigo-900">
                            Click to upload or drag & drop
                          </p>
                          <p className="text-sm text-indigo-600 mt-2">
                            JPG, PNG, GIF, WebP up to 50MB
                          </p>
                        </label>
                      </div>

                      {imageFile && (
                        <div className="bg-gradient-to-r from-indigo-100 to-pink-100 border border-indigo-300 rounded-xl p-5 text-indigo-900 font-medium">
                          Selected: <strong>{imageFile.name}</strong> (
                          {(imageFile.size / 1024 / 1024).toFixed(1)} MB)
                        </div>
                      )}
                    </div>
                  )}

                  {/* URL Tab */}
                  {activeTab === "url" && (
                    <div className="space-y-6">
                      <input
                        type="url"
                        placeholder="https://example.com/photo.jpg"
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        className="w-full text-indigo-700 px-5 py-4 text-lg border-2 border-indigo-300 rounded-xl focus:outline-none focus:border-indigo-600 focus:ring-4 focus:ring-indigo-200"
                      />
                      <p className="text-sm text-indigo-700">
                        Supports direct links, Google Drive (public), Imgur,
                        Twitter, Instagram, etc.
                      </p>
                    </div>
                  )}
                </>
              )}

              {isAnalyzing && (
                <div className="flex flex-col items-center justify-center py-32 space-y-10">
                  <div className="relative">
                    <div className="absolute inset-0 rounded-full bg-indigo-500 opacity-20 animate-ping"></div>
                    <div className="absolute inset-0 rounded-full bg-indigo-500 opacity-40 animate-ping delay-500"></div>
                    <div className="relative w-40 h-40">
                      <Sparkles
                        size={100}
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-indigo-600 animate-pulse"
                      />
                      <Sparkles
                        size={40}
                        className="absolute top-2 left-1/2 -translate-x-1/2 text-pink-500 animate-spin"
                        style={{ animation: "spin 3s linear infinite" }}
                      />
                      <Sparkles
                        size={32}
                        className="absolute bottom-4 right-4 text-purple-500 animate-bounce"
                      />
                    </div>
                  </div>

                  <div className="text-center max-w-md">
                    <h2 className="text-4xl font-bold text-indigo-900 mb-4">
                      Analyzing Your Image…
                    </h2>
                    <p className="text-lg text-indigo-700">
                      Running advanced AI forensics detection across multiple
                      models
                    </p>
                  </div>

                  <div className="w-full max-w-sm bg-indigo-100 rounded-full h-4 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-indigo-600 via-purple-500 to-pink-500 rounded-full animate-pulse"
                      style={{
                        width: "100%",
                        animation:
                          "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Analyze Button */}
            <div className="px-8 pb-8">
              <div className="pt-8 border-t border-indigo-100">
                <button
                  onClick={handleAnalyze}
                  disabled={
                    isAnalyzing ||
                    (activeTab === "upload" && !imageFile) ||
                    (activeTab === "url" && !imageUrl.trim())
                  }
                  className={`w-full flex items-center justify-center gap-4 py-5 rounded-2xl font-bold text-white text-xl transition-all transform ${
                    isAnalyzing
                      ? "bg-indigo-400 cursor-wait"
                      : "bg-indigo-600 hover:-translate-y-1 hover:shadow-2xl"
                  } disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0`}
                >
                  {isAnalyzing ? (
                    <>
                      <div className="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                      Analyzing Image...
                    </>
                  ) : (
                    <>
                      <Sparkles size={28} />
                      Detect AI Image
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="bg-indigo-50 border-t border-indigo-200 p-8 text-indigo-800">
              <div className="flex gap-4">
                <AlertCircle
                  size={24}
                  className="text-indigo-600 flex-shrink-0"
                />
                <div className="text-sm">
                  <p className="font-bold text-indigo-900 mb-2">
                    Supported Sources
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Direct image files (JPG, PNG, WebP, GIF)</li>
                    <li>Google Drive, Dropbox (public links)</li>
                    <li>Any publicly accessible image URL</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {result && (
        <ImageResultModal result={result} onClose={() => setResult(null)} />
      )}
    </>
  );
};

export default ImageDemo;
